import { observable, runInAction } from 'mobx'

export interface Action<ActionArgs = undefined, ActionResponse = undefined> {
  (args: ActionArgs): Promise<ActionResponse>
  inProgress: boolean
  args?: ActionArgs
  response?: ActionResponse
  error?: Error
  mode?: ActionMode
  reset: () => void
}

export type ActionMode =
  | 'single' // Can not be invoked while another invoke is still in progress (throws error when a previous invoke is still in progress)
  | 'queue' // Invokes are queued; and executed serially

export function action<ActionArgs = void, ActionResponse = void>(
  callFn: (args?: ActionArgs) => Promise<ActionResponse>,
  config?: {
    mode: ActionMode
  },
): Action<ActionArgs, ActionResponse> {
  const _inProgress = observable.box(false)
  const _args = observable.box<ActionArgs>()
  const _response = observable.box<ActionResponse>()
  const _error = observable.box<Error>()
  const mode: ActionMode = (config ? config.mode : undefined) || 'single'
  let lastCall: Promise<ActionResponse | undefined> = Promise.resolve(undefined)
  const func: (args: ActionArgs) => Promise<ActionResponse | undefined> = async (args: ActionArgs) => {
    const prevCall = lastCall
    async function fn() {
      const inProgress = runInAction(() => _inProgress.get())
      if (inProgress) {
        if (mode === 'single') {
          throw new Error(
            `Operation already in progress, new: ${JSON.stringify(args)}, inProgress ${JSON.stringify(_args.get())}`,
          )
        } else {
          await prevCall
        }
      }
      runInAction(() => {
        _inProgress.set(true)
        _args.set(args)
        _response.set(undefined)
        _error.set(undefined)
      })
      try {
        const response = await callFn(args)
        runInAction(() => {
          _response.set(response)
          _inProgress.set(false)
        })
        return response
      } catch (err) {
        const error = err as Error
        runInAction(() => {
          _inProgress.set(false)
          _error.set(error)
        })
        throw err
      }
    }
    lastCall = fn()
    return lastCall
  }
  Object.defineProperties(func, {
    inProgress: {
      get() {
        return _inProgress.get()
      },
    },
    args: {
      get() {
        return _args.get()
      },
    },
    response: {
      get() {
        return _response.get()
      },
    },
    error: {
      get() {
        return _error.get()
      },
    },
    mode: {
      get(): ActionMode {
        return mode
      },
    },
  })

  return func as Action<ActionArgs, ActionResponse>
}
